import styles from './EnvironmentalSafetyAssessmentPDF.module.scss';
import BasicBreadcrumbs from '../../../components/Breadcrumb/MuiBreadcrumb';
import { LineOfBusinessEnum } from '../../../constants/BusinessConstants';
import { Card, CardContent, Grid, Button, TableContainer, Table, TableHead, TableRow, TableCell, Paper, TableBody } from '@mui/material';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ItemLabel from "../../../components/ItemLabel/ItemLabel";
import { Box } from '@mui/system';
import { HTTPMethod, callApi as pdfRequest } from '../../../services/HttpService/HttpService';
import { OrdersApi } from '../../../constants/ApiConstants';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AssessmentType, RecommendationStatusEnum } from '../../../common/enums';
import withTitle from '../../../components/withTitle/withTitle';
import NoData from '../../../components/noData/noData';
import loader from "../../../../src/assets/loader.gif";
import Container from "@mui/material/Container";

const EnvironmentalSafetyAssessmentPDF = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoadingAssessmentData, setIsLoadingAssessmentData] = useState(false);
  const [assessmentData, setAssessmentData] = useState({
    safetyAssessment: null,
    environmentalCondition: [],
    smokingEnvironment: [],
    careGiver: [],
    suitabilityForEquipment: [],
    impairments: [],
    safetyRecommendations: [],
    hasRecommendation: false
  });

  const lineOfBusinessPath = LineOfBusinessEnum.patientsToMembers;
  const userId = useSelector(state => state.authState.userData?.data?.user_info?.user_id ?? 0);
  const companyId = useSelector(state => state.authState.userData?.data?.company_settings?.[0]?.company_id ?? 0);
  const orderId = location.state?.order_id || location?.pathname?.split("/").pop();
  const patientName = location.state?.patientDetailsName;
  const checkUrlIsOrder = location.pathname.includes("orders");
  const breadcrumbItems = checkUrlIsOrder
    ? [{ label: "Orders", link: "/orders" }, { label: orderId }]
    : [{ label: lineOfBusinessPath, link: `/${lineOfBusinessPath.toLowerCase()}` }, { label: patientName }];

  const handleSafetyAssessmentPdf = async () => {
    setIsLoadingAssessmentData(true);
    const requestBody = { order_id: orderId, company_id: companyId, user_id: userId };
    const response = await pdfRequest(HTTPMethod.Post, OrdersApi.safetyCheckList, requestBody);
    if (response?.status_code === 200 && response?.data?.esa_report_response?.[0]) {
      const report = response?.data?.esa_report_response[0];
      const esaReport = report?.esa_report;
      setAssessmentData({
        safetyAssessment: report,
        environmentalCondition: esaReport?.filter(data => data?.type === AssessmentType.EnvironmentalCondition && data?.recommendation_value !== null)?.sort((a, b) => a.sequence - b.sequence),
        smokingEnvironment: esaReport?.filter(data => data?.type === AssessmentType.SmokingEnvironment)?.sort((a, b) => a.sequence - b.sequence),
        careGiver: esaReport?.filter(data => data?.type === AssessmentType.CareGiver && data?.value === "Yes"),
        suitabilityForEquipment: esaReport?.filter(data => data?.type === AssessmentType.SuitabilityForEquipment && data?.value === "Yes"),
        impairments: esaReport?.filter(data => data?.type === AssessmentType.Impairments && data?.value === "Yes"),
        safetyRecommendations: esaReport?.filter(data => data?.type === AssessmentType.EnvironmentalCondition && data?.question?.trim() === "Safety recommendations left in the home"),
        hasRecommendation: esaReport?.some(item => item?.type === AssessmentType.EnvironmentalCondition && item?.recommendation_text?.trim() && item?.recommendation_text !== "N/A")
      });
      setIsLoadingAssessmentData(false)
    }
  };

  const handleDetailsPage = () => {
    if (location?.pathname?.includes('order')) {
      navigate(`/orders/order-details/${orderId}`);
    } else {
      navigate(`/${lineOfBusinessPath.toLowerCase()}/${LineOfBusinessEnum.patientToMember.toLowerCase()}-details/${safetyAssessment?.esa?.patient_id}`)
    }
  }

  useEffect(() => {
    handleSafetyAssessmentPdf();
  }, []);

  const { safetyAssessment, environmentalCondition, smokingEnvironment, careGiver, suitabilityForEquipment, impairments, safetyRecommendations, hasRecommendation } = assessmentData;

  return (
    <div data-testid="EnvironmentalSafetyAssessmentPDF">
      <Container className="mainContainer">
      <BasicBreadcrumbs items={breadcrumbItems} />
      {safetyAssessment ?
        <div className="print-section">
          <div className={styles.heading}>
            <ArrowBackIosIcon sx={{ cursor: "pointer" }} className={styles.hideArrow} data-testid="backButton" onClick={() => handleDetailsPage()} />
            <p><span>{t('Prescriptions.environmentalSafetyAssessment')}</span></p>
          </div>
          <Card variant="outlined" className={`marginBottomTwenty ${styles.card}`}>
            <CardContent className={styles.customCardContent}>
              <Grid container>
                <Grid item xs={12} lg={6}>
                  <ItemLabel label="Patient name" text={safetyAssessment?.esa?.patient_name} />
                  {safetyAssessment?.esa?.patient_id && <ItemLabel
                    label="Patient ID"
                    text={<>{checkUrlIsOrder
                      ? <Link to={`/${LineOfBusinessEnum.patientsToMembers.toLowerCase()}/${LineOfBusinessEnum.patientToMember.toLowerCase()}-details/${safetyAssessment?.esa?.patient_id}`} className={styles.customLink}>{safetyAssessment?.esa?.patient_id}</Link>
                      : safetyAssessment?.esa?.patient_id}</>} />}
                  {orderId && <ItemLabel
                    label="Order ID"
                    text={<>{!checkUrlIsOrder
                      ? <Link to={`/orders/order-details/${orderId}`} className={styles.customLink}>{orderId}</Link>
                      : orderId}</>} />}
                  <ItemLabel label="Completed on" text={safetyAssessment?.esa?.completed_on} />
                  <ItemLabel label="Technician" text={safetyAssessment?.esa?.technician_name} />
                  <ItemLabel label="Suitable for equipment" text={suitabilityForEquipment?.[0]?.question} highlightRed={(suitabilityForEquipment?.[0]?.question === 'Poor' || suitabilityForEquipment?.[0]?.question === 'Fair') ? true : false} />
                </Grid>
                <Grid item xs={12} lg={6} className={styles.buttonContainer}>
                  <Grid item className={styles.buttonStyling}>
                    <Button color='primary' data-testid="printButton" onClick={() => window.print()}>
                      <LocalPrintshopRoundedIcon className='marginRightFive' />{t("Prescriptions.print")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {environmentalCondition?.length > 0 && <Card variant="outlined" className={`marginBottomTwenty ${styles.card}`}>
            <Box className={styles.scopedClassName}><div className={styles.scopedClassHeading}> {t("Prescriptions.environmentalSafety")}</div></Box>
            <CardContent className={styles.customCardContent}>
              <Grid container>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small" aria-label="environmental safety table">
                    <TableHead>
                      <TableRow>
                        <TableCell className={`${styles.headerCell} ${styles.cellNoPadding}`}><strong> {t("NewOrder.description")}</strong></TableCell>
                        <TableCell className={`${styles.headerCell} ${styles.cellNoPadding} ${styles.cellPaddingX}`} width="180px"><strong> {t("Prescriptions.assessment")}</strong></TableCell>
                        <TableCell className={`${styles.headerCell} ${styles.cellNoPadding} ${styles.cellPaddingX}`}><strong> {t("Prescriptions.recommendation")}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {environmentalCondition?.map(item => {
                        const assessmentText =
                          item?.question === "Use of extension cords and power strips"
                            ? item?.value === "Yes" ? "Not acceptable" : item?.value === "No" ? "Acceptable" : "N/A"
                            : item?.value != null ? RecommendationStatusEnum[item.value] || "N/A" : "N/A";
                        return (
                          <TableRow key={item.feedback_id}>
                            <TableCell className={styles.cellNoPadding}>{item?.question}</TableCell>
                            <TableCell className={`${assessmentText === 'Not acceptable' ? styles.notAcceptable : ''} ${styles.cellNoPadding} ${styles.cellPaddingX}`}>
                              {assessmentText}
                            </TableCell>
                            <TableCell className={`${styles.cellNoPadding} ${styles.cellPaddingX}`}>{item?.recommendation_text || 'N/A'}</TableCell>
                          </TableRow>
                        )
                      }
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <div className='marginTop10px'>
                  <ItemLabel label="Safety recommendations left in the home" text={safetyRecommendations?.[0]?.value || 'N/A'} />
                </div>
              </Grid>
            </CardContent>
          </Card>}

          {smokingEnvironment?.length > 0 && <Card variant="outlined" className={`marginBottomTwenty ${styles.card}`}>
            <Box className={styles.scopedClassName}><div className={styles.scopedClassHeading}> {t("Prescriptions.smokingEnvironment")}</div></Box>
            <CardContent className={styles.customCardContent}>
              <Grid container>
                {smokingEnvironment?.map(item => (
                  <Grid item xs={12} key={item?.feedback_id}>
                     <ItemLabel label={item?.question.replace('?','')} text={item?.value || 'N/A'} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>}

          {(careGiver?.[0]?.question || impairments?.[0]?.question || safetyAssessment?.esa_report[0]?.additional_comment) && <Card variant="outlined" className={`marginBottomTwenty ${styles.card}`}>
            <Box className={styles.scopedClassName}><div className={styles.scopedClassHeading}> {t("Prescriptions.otherInformation")}</div></Box>
            <CardContent className={styles.customCardContent}>
              <Grid container>
                 {careGiver?.[0]?.question && (
                    <Grid item xs={12}>
                      <ItemLabel
                        label="Caregiver"
                        text={
                          careGiver?.length > 0
                            ? careGiver.map((item) => item?.question || 'N/A').join(', ')
                            : 'N/A'
                        }
                      />
                    </Grid>
                  )}
                   {impairments?.[0]?.question && (
                    <Grid item xs={12}>
                      <ItemLabel
                        label="Impairments"
                        text={
                          impairments?.length > 0
                            ? impairments.map((item) => item?.question || 'N/A').join(', ')
                            : 'N/A'
                        }
                      />
                    </Grid>
                  )}
                {safetyAssessment?.esa_report[0]?.additional_comment && <Grid item xs={12}><ItemLabel label="Additional comments" text={safetyAssessment?.esa_report[0]?.additional_comment || 'N/A'} /></Grid>}
              </Grid>
            </CardContent>
          </Card>}

          <div className={`${styles.textStyle} marginBottomTwenty`}>
           {safetyAssessment.esa_report[0].company_name} {t("Prescriptions.pdfFirstParagraph")} {hasRecommendation && t("Prescriptions.pdfSecondParagraph")} {t("Prescriptions.pdfThirdParagraph")}
          </div>

          <div className={`${styles.textStyle} marginBottomTwenty`}>
            {t("Prescriptions.consentAcknowledgement").replace('#companyName', safetyAssessment.esa_report[0].company_name)}
            <strong> {t("Prescriptions.equipmentConditionConsent")}</strong>
          </div>

          <div className={styles.signatureSection}>
            <div className={styles.signatureBlock}>
              <div><img src={safetyAssessment?.esa?.signature} alt="Signature" className={styles.signatureImg} /></div>
              <div className={styles.signatureLine}></div>
              <div className={styles.signatureLabel}>{LineOfBusinessEnum.patientToMember}{t("Prescriptions.caregiverSignature")}</div>
            </div>
            <div className={styles.signatureBlock}>
              <div className={styles.printedName}>{safetyAssessment?.esa?.received_by}</div>
              <div className={styles.signatureLine}></div>
              <div className={styles.signatureLabel}>{LineOfBusinessEnum.patientToMember}{t("Prescriptions.caregiverPrintedName")}</div>
            </div>
          </div>
        </div> :
        <div className='marginTopThirty'>
          {isLoadingAssessmentData && (
            <NoData
              message={isLoadingAssessmentData && <img alt="Loading..." src={loader} />}
            />
          )}
        </div>}
      </Container>
    </div>
  );
};

export default withTitle(EnvironmentalSafetyAssessmentPDF, "Environmental Safety Assessment");